import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, X, Route, Wand2, Loader2, Footprints } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useCrawl } from "@/context/CrawlContext";
import { VenueMap } from "@/components/VenueMap";
import { ShareButton } from "@/components/ShareButton";
import { haversine, fmtWindow, todayKey } from "@/lib/hh";

export const routeMiles = (stops) => stops.slice(1).reduce((t, s, i) => t + haversine(stops[i], s), 0);

function optimize(stops) {
  if (stops.length < 3) return stops;
  const rest = [...stops.slice(1)];
  const out = [stops[0]];
  while (rest.length) {
    const last = out[out.length - 1];
    rest.sort((a, b) => haversine(last, a) - haversine(last, b));
    out.push(rest.shift());
  }
  return out;
}

export function StopList({ stops, onMove, onRemove }) {
  return (
    <ol className="space-y-3">
      {stops.map((s, i) => (
        <li key={s.id} data-testid={`crawl-stop-${i}`} className="panel flex items-center gap-3 p-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fde68a] font-extrabold text-zinc-950">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <Link to={`/venue/${s.id}`} className="block truncate font-semibold text-white hover:text-[#fde68a]">{s.name}</Link>
            <p className="truncate text-xs text-zinc-500">{fmtWindow(s.hours?.[todayKey()])} · {s.address}</p>
          </div>
          {onMove && (
            <div className="flex gap-1">
              <button data-testid={`crawl-up-${i}`} onClick={() => onMove(i, -1)} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800" aria-label="Move up"><ArrowUp size={15} /></button>
              <button data-testid={`crawl-down-${i}`} onClick={() => onMove(i, 1)} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800" aria-label="Move down"><ArrowDown size={15} /></button>
              <button data-testid={`crawl-remove-${i}`} onClick={() => onRemove(s)} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800" aria-label="Remove"><X size={15} /></button>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

export default function Crawl() {
  const { stops, setStops, move, toggle, clear } = useCrawl();
  const [name, setName] = useState("My Bar Crawl");
  const [saving, setSaving] = useState(false);
  const [shared, setShared] = useState(null);
  const miles = routeMiles(stops);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/crawls", { name, venue_ids: stops.map((s) => s.id) });
      setShared(data);
      toast.success("Crawl link ready to share");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <p className="eyebrow">Plan the night</p>
      <h1 className="font-heading mt-2 text-5xl text-white sm:text-6xl">Bar Crawl Builder</h1>
      {!stops.length ? (
        <div data-testid="crawl-empty" className="panel mt-8 p-8 text-zinc-300">
          <Route className="mb-3 text-[#fde68a]" />
          Tap <span className="font-bold text-white">+ Crawl</span> on any venue to add it here. Pick 2 or more to build a walking route.
          <Link to="/" className="mt-3 block font-semibold text-[#fde68a] hover:underline">Find venues →</Link>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            <Input data-testid="crawl-name-input" value={name} onChange={(e) => { setName(e.target.value); setShared(null); }} className="h-11 bg-zinc-950" maxLength={80} />
            <p data-testid="crawl-distance" className="flex items-center gap-2 text-sm text-zinc-300">
              <Footprints size={16} className="text-[#fde68a]" /> {stops.length} stops · about {miles.toFixed(1)} mi walking (straight-line)
            </p>
            <StopList stops={stops} onMove={(i, d) => { move(i, d); setShared(null); }} onRemove={(s) => { toggle(s); setShared(null); }} />
            <div className="flex flex-wrap gap-2">
              <button data-testid="crawl-optimize" onClick={() => { setStops(optimize(stops)); setShared(null); }} className="chip"><Wand2 size={14} /> Shortest order</button>
              <button data-testid="crawl-clear" onClick={() => { clear(); setShared(null); }} className="chip">Clear all</button>
            </div>
            {shared ? (
              <div data-testid="crawl-share-ready" className="panel flex flex-wrap items-center justify-between gap-3 p-4">
                <Link to={`/crawl/${shared.id}`} className="text-sm font-semibold text-[#fde68a] hover:underline">View shared crawl →</Link>
                <ShareButton title={shared.name} text={`Join my bar crawl: ${shared.name}`} path={`/crawl/${shared.id}`} testId="crawl-share-button" label="Share crawl" />
              </div>
            ) : (
              <button data-testid="crawl-save-button" disabled={stops.length < 2 || saving} onClick={save} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#fde68a] font-bold text-zinc-950 disabled:opacity-40">
                {saving && <Loader2 size={16} className="animate-spin" />} {stops.length < 2 ? "Add at least 2 stops" : "Create share link"}
              </button>
            )}
          </div>
          <div className="lg:col-span-3">
            <VenueMap venues={stops} route className="h-[60vh] min-h-[380px]" />
          </div>
        </div>
      )}
    </div>
  );
}
